/**
 * Help centre: the articles customers can read, the one that answers a screen's help key, and
 * their "was this helpful" feedback. The server decides which articles a person may see (by their
 * role), so nothing here filters the lists.
 */
import { authedPortal } from "./api";

export interface HelpArticleSummary {
  slug: string;
  title: string;
  summary: string;
  category: string;
  tags: string[];
}

export interface HelpArticle extends HelpArticleSummary {
  /** Markdown: read it with `parseMarkdown` (`lib/help-markdown.ts`), never as HTML. */
  body_md: string;
  /** The help keys (`lib/help-context.ts`) this article answers. */
  context_keys: string[];
  updated_at: string;
}

export interface HelpArticleList {
  articles: HelpArticleSummary[];
  /** The categories of the articles this person can see, for the filter. */
  categories: string[];
}

export interface HelpArticleFilters {
  q?: string;
  category?: string;
}

export interface HelpFeedbackInput {
  helpful: boolean;
  comment?: string | null;
}

const enc = encodeURIComponent;
const base = (orgId: string) => `/v1/organizations/${enc(orgId)}/help`;

export const helpApi = {
  articles: (orgId: string, filters: HelpArticleFilters = {}) =>
    authedPortal<HelpArticleList>(`${base(orgId)}/articles`, {
      query: { q: filters.q, category: filters.category },
    }),
  article: (orgId: string, slug: string) => authedPortal<HelpArticle>(`${base(orgId)}/articles/${enc(slug)}`),
  /** The article for a help key; a 404 `ApiError` when none answers it. Keys are lower-case. */
  context: (orgId: string, key: string) =>
    authedPortal<HelpArticle>(`${base(orgId)}/context/${enc(key.toLowerCase())}`),
  feedback: (orgId: string, slug: string, body: HelpFeedbackInput) =>
    authedPortal<void>(`${base(orgId)}/articles/${enc(slug)}/feedback`, { method: "PUT", body }),
};

/** Cache keys, each starting with the organization id so nothing cached for one business shows under another. */
export const helpKeys = {
  all: (orgId: string) => ["help", orgId] as const,
  articles: (orgId: string, q: string, category: string | undefined) =>
    ["help", orgId, "articles", q, category ?? ""] as const,
  article: (orgId: string, slug: string) => ["help", orgId, "article", slug] as const,
  context: (orgId: string, key: string) => ["help", orgId, "context", key] as const,
};
